import {
  AlertCircle,
  CheckCircle,
  Clock,
  PhoneCall,
  PhoneOff,
  RefreshCw,
  X,
} from 'lucide-react-native';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { HStack, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import type { AutoCallStatus } from '../api/voiceApi';

interface CallStatusBannerProps {
  status: AutoCallStatus;
  onRetry: () => void;
  onCancel: (logId: string) => void;
  isRetrying?: boolean;
  isCancelling: boolean;
}

const dispositionConfig: Record<
  string,
  { label: string; color: string; bg: string; icon: typeof CheckCircle }
> = {
  initiated: {
    label: 'Call queued',
    color: colors.status.info.main,
    bg: colors.status.info.bg,
    icon: Clock,
  },
  ringing: {
    label: 'Calling...',
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    icon: PhoneCall,
  },
  answered: {
    label: 'Call answered',
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    icon: CheckCircle,
  },
  voicemail: {
    label: 'Voicemail left',
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    icon: PhoneOff,
  },
  no_answer: {
    label: 'No answer',
    color: colors.status.error.main,
    bg: colors.status.error.bg,
    icon: PhoneOff,
  },
  busy: {
    label: 'Line busy',
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    icon: PhoneOff,
  },
  failed: {
    label: 'Call failed',
    color: colors.status.error.main,
    bg: colors.status.error.bg,
    icon: AlertCircle,
  },
  cancelled: {
    label: 'Call cancelled',
    color: colors.text.tertiary,
    bg: colors.background.tertiary,
    icon: X,
  },
  rejected: {
    label: 'Call rejected',
    color: colors.status.error.main,
    bg: colors.status.error.bg,
    icon: PhoneOff,
  },
};

const fallbackConfig = {
  label: 'Unknown',
  color: colors.text.tertiary,
  bg: colors.background.tertiary,
  icon: AlertCircle,
};

export function CallStatusBanner({
  status,
  onRetry,
  onCancel,
  isRetrying,
  isCancelling,
}: CallStatusBannerProps) {
  const config = dispositionConfig[status.disposition] ?? fallbackConfig;
  const Icon = config.icon;

  const formattedTime = new Date(status.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const durationText = status.durationSeconds != null ? `${status.durationSeconds}s` : null;

  return (
    <View style={[styles.container, { backgroundColor: config.bg }]}>
      <HStack style={styles.row}>
        <HStack style={styles.statusSection}>
          {status.isPending ? (
            <ActivityIndicator size="small" color={config.color} />
          ) : (
            <Icon size={18} color={config.color} strokeWidth={2} />
          )}
          <VStack style={styles.textSection}>
            <Text style={[styles.statusLabel, { color: config.color }]}>{config.label}</Text>
            <Text style={styles.meta}>
              {formattedTime}
              {durationText ? ` \u00B7 ${durationText}` : ''}
              {status.attemptNumber > 1 ? ` \u00B7 Attempt ${status.attemptNumber}` : ''}
            </Text>
          </VStack>
        </HStack>

        <HStack style={styles.actions}>
          {status.isPending && (
            <Pressable
              onPress={() => onCancel(status.id)}
              disabled={isCancelling}
              style={[styles.actionButton, styles.cancelButton]}
              accessibilityLabel="Cancel call"
              accessibilityRole="button"
            >
              {isCancelling ? (
                <ActivityIndicator size={14} color={colors.status.error.main} />
              ) : (
                <X size={14} color={colors.status.error.main} strokeWidth={2} />
              )}
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>
          )}

          {status.canRetry && (
            <Pressable
              onPress={onRetry}
              disabled={isRetrying}
              style={[styles.actionButton, styles.retryButton]}
              accessibilityLabel="Retry call"
              accessibilityRole="button"
            >
              {isRetrying ? (
                <ActivityIndicator size={14} color={colors.primary.main} />
              ) : (
                <RefreshCw size={14} color={colors.primary.main} strokeWidth={2} />
              )}
              <Text style={styles.retryText}>Retry</Text>
            </Pressable>
          )}
        </HStack>
      </HStack>

      {status.errorMessage && !status.isPending && (
        <Text style={styles.errorText}>{status.errorMessage}</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginBottom: 12,
  },
  row: {
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  statusSection: {
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  textSection: {
    gap: 2,
    flex: 1,
  },
  statusLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  meta: {
    fontSize: 11,
    color: colors.text.tertiary,
  },
  actions: {
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  cancelButton: {
    backgroundColor: colors.status.error.bg,
  },
  retryButton: {
    backgroundColor: colors.primary.muted,
  },
  cancelText: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.status.error.main,
  },
  retryText: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.primary.main,
  },
  errorText: {
    fontSize: 11,
    color: colors.status.error.light,
    marginTop: 6,
  },
});
